import { useSyncExternalStore } from 'react';

/**
 * Light, dark or the system's choice, remembered in this browser. The
 * choice sets data-theme on <html>; "system" leaves it to the media query.
 */
export type ThemePreference = 'system' | 'light' | 'dark';

const KEY = 'oppy.theme';
const listeners = new Set<() => void>();

export function readTheme(): ThemePreference {
  try {
    const value = localStorage.getItem(KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

export function applyTheme(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') delete root.dataset.theme;
  else root.dataset.theme = preference;
}

export function setTheme(preference: ThemePreference): void {
  try {
    if (preference === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, preference);
  } catch {
    // private mode: the choice lasts for this page only
  }
  applyTheme(preference);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme(): [ThemePreference, (preference: ThemePreference) => void] {
  const preference = useSyncExternalStore(subscribe, readTheme, () => 'system' as const);
  return [preference, setTheme];
}
